import { useState } from 'react';
import { api, extractErrorMessage } from '@/lib/api-client';

export type UploadPurpose =
  | 'vehicle_crlv'
  | 'vehicle_document'
  | 'driver_cnh'
  | 'driver_document'
  | 'checklist_photo'
  | 'fueling_receipt'
  | 'fine_notification'
  | 'signature'
  | 'other';

interface PresignResponse {
  data: {
    uploadId: string;
    url: string;
    method: 'PUT';
    headers: Record<string, string>;
    expiresAt: string;
    maxSizeBytes: number;
  };
}

export interface UploadedFile {
  uploadId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: 'committed';
}

export interface FileUploadState {
  phase: 'idle' | 'presigning' | 'uploading' | 'committing' | 'done' | 'error';
  progress: number; // 0..100
  error?: string;
}

/**
 * Hook que orquestra o fluxo completo de upload:
 *   1. POST /uploads/presign   → recebe URL pré-assinada
 *   2. PUT direto no MinIO/S3  → envia os bytes (com progresso)
 *   3. POST /uploads/:id/commit → confirma no servidor
 *
 * Retorna `upload(file, purpose, related?)` e o estado atual `state`.
 * Um hook por operação de upload (use um `useState` externo se precisar gerenciar múltiplos).
 */
export function useFileUpload() {
  const [state, setState] = useState<FileUploadState>({ phase: 'idle', progress: 0 });

  const reset = () => setState({ phase: 'idle', progress: 0 });

  const upload = async (
    file: File,
    purpose: UploadPurpose,
    related?: { relatedType?: string; relatedId?: string },
  ): Promise<UploadedFile> => {
    try {
      setState({ phase: 'presigning', progress: 0 });

      // 1. Presign
      const presignRes = await api.post<PresignResponse>('/uploads/presign', {
        purpose,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        relatedType: related?.relatedType,
        relatedId: related?.relatedId,
      });
      const { uploadId, url } = presignRes.data.data;

      // 2. PUT direto no MinIO com XHR (para ter progresso)
      setState({ phase: 'uploading', progress: 0 });
      await putWithProgress(url, file, (pct) => {
        setState({ phase: 'uploading', progress: pct });
      });

      // 3. Commit
      setState({ phase: 'committing', progress: 100 });
      await api.post(`/uploads/${uploadId}/commit`);

      setState({ phase: 'done', progress: 100 });
      return {
        uploadId,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        status: 'committed',
      };
    } catch (err) {
      const msg = extractErrorMessage(err);
      setState({ phase: 'error', progress: 0, error: msg });
      throw new Error(msg);
    }
  };

  return { state, upload, reset };
}

/**
 * PUT de binário em URL pré-assinada com callback de progresso.
 * Usa XMLHttpRequest porque Axios não expõe `upload.onprogress` de forma
 * tão limpa e o fluxo é trivial o suficiente pra não justificar.
 */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`PUT falhou (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Falha de rede ao enviar arquivo'));
    xhr.onabort = () => reject(new Error('Upload cancelado'));
    xhr.send(file);
  });
}
